import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNow, isPast } from 'date-fns';
import { ArrowLeft, Clock, Code2, FolderGit2, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CircularGauge } from '@/components/ui/circular-gauge';
import { ProjectSubmissionDialog } from '@/features/Hackathons/ProjectSubmissionDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCurrentBatch } from '@/lib/hooks/useCurrentBatch';
import {
  useStudentHackathonDetail,
  useStudentHackathonComponents,
  useStudentHackathonLeaderboard,
  useMyHackathonTeam,
} from '@/lib/hooks/useHackathons';
import type { Hackathon, HackathonStatus } from '@/lib/api/hackathons';

// A hackathon belongs to exactly one batch, but "All Courses" mode has no
// single current batch to assume — the list page (index.tsx) already knows
// each card's batch_id, so it's passed through as a search param rather than
// guessed here (guessing batches[0], as an earlier version of this feature
// did elsewhere, is exactly the "All Courses" bug this session fixed).
export const Route = createFileRoute('/_student/hackathons/$hackathonId')({
  validateSearch: (search: Record<string, unknown>): { batchId?: string } => ({
    batchId: typeof search.batchId === 'string' ? search.batchId : undefined,
  }),
  component: StudentHackathonDetailPage,
});

// Per-status hero styling: a wordless colored dot (echoing the dot+label
// convention used elsewhere for due-date/session states), an eyebrow label
// distinct from the statusMessage copy below it (so the two never collide
// on the same phrase), and a soft tint so the hero reads as "urgent" (live),
// "upcoming" (scheduled), or neutral (draft/completed) at a glance.
const STATUS_META: Record<HackathonStatus, { dot: string; eyebrow: string; tint: string }> = {
  draft: { dot: 'bg-[#c0b8b0]', eyebrow: 'Not Started', tint: 'from-[#f2f0ed] to-white' },
  scheduled: { dot: 'bg-amber-400', eyebrow: 'Scheduled', tint: 'from-amber-50 to-white' },
  live: { dot: 'bg-emerald-500 animate-pulse', eyebrow: 'In Progress', tint: 'from-emerald-50 to-white' },
  completed: { dot: 'bg-[#c0b8b0]', eyebrow: 'Completed', tint: 'from-[#f2f0ed] to-white' },
};

// Explains *why* submissions are open/closed in plain language — a disabled
// button with no reason is exactly the confusion this addresses (students
// otherwise can't tell "not started yet" apart from "already over").
function statusMessage(hackathon: Hackathon): string {
  const starts = new Date(hackathon.start_at);
  const ends = new Date(hackathon.end_at);
  switch (hackathon.status) {
    case 'draft':
      return "Your trainer is still setting this hackathon up — it's not open yet.";
    case 'scheduled':
      return isPast(starts)
        ? 'This hackathon is about to start — hang tight.'
        : `Starts in ${formatDistanceToNow(starts)}. Submissions open once it goes live.`;
    case 'live':
      return isPast(ends)
        ? 'Time is up — final scores are being tallied.'
        : `Live now — ${formatDistanceToNow(ends)} left to submit.`;
    case 'completed':
      return 'This hackathon has ended. Submissions are closed.';
  }
}

export function StudentHackathonDetailPage() {
  const { hackathonId } = Route.useParams();
  const { batchId: searchBatchId } = Route.useSearch();
  const { batchId: currentBatchId } = useCurrentBatch();
  // Prefer the batch the list page told us this hackathon belongs to; fall
  // back to the currently-selected single batch (e.g. direct navigation to
  // this URL outside "All Courses" mode).
  const batchId = searchBatchId ?? currentBatchId ?? '';

  const { data: hackathon, isLoading } = useStudentHackathonDetail(batchId, hackathonId);
  const { data: components } = useStudentHackathonComponents(batchId, hackathonId);
  const { data: leaderboard } = useStudentHackathonLeaderboard(batchId, hackathonId);
  const { data: myTeam } = useMyHackathonTeam(batchId, hackathonId);

  // Which project component's submission dialog is open (null = none). Mirrors
  // the local-state open/close pattern HackathonCreateDialog established.
  const [submitComponentId, setSubmitComponentId] = useState<string | null>(null);

  if (isLoading) {
    return <div className="mx-auto max-w-[1200px] p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  if (!hackathon) {
    return (
      <div className="mx-auto max-w-[1200px] p-6">
        <p className="text-sm text-muted-foreground">Hackathon not found.</p>
      </div>
    );
  }

  const meta = STATUS_META[hackathon.status] ?? STATUS_META.draft;
  const pctElapsed = (() => {
    const start = new Date(hackathon.start_at).getTime();
    const end = new Date(hackathon.end_at).getTime();
    const now = Date.now();
    if (end <= start) return 0;
    return ((now - start) / (end - start)) * 100;
  })();

  return (
    <div className="mx-auto max-w-[900px] p-6">
      <Link
        to="/hackathons"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Hackathons
      </Link>

      {/* Hero: status is communicated three ways at once (dot+eyebrow, a
          plain-language sentence, and — for live hackathons — a live
          countdown ring) so "why is Submit disabled / why does it say
          Draft" never needs a second guess. */}
      <div className={`mb-6 rounded-2xl border border-[#e8e4df] bg-gradient-to-br ${meta.tint} p-6`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2">
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
              <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                {meta.eyebrow}
              </span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground">{hackathon.title}</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              {new Date(hackathon.start_at).toLocaleString()} &rarr; {new Date(hackathon.end_at).toLocaleString()}
            </p>
          </div>
          {hackathon.status === 'live' && (
            <CircularGauge value={pctElapsed} label="Time Elapsed" unit="%" size={84} color="#22c55e" />
          )}
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/70 px-3 py-2.5 text-sm text-muted-foreground">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{statusMessage(hackathon)}</span>
        </div>

        {hackathon.description && (
          <p className="mt-3 text-sm text-muted-foreground">{hackathon.description}</p>
        )}
      </div>

      {myTeam && (
        <div className="mb-6 rounded-xl border border-[#e8e4df] bg-white p-5">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Your Team</p>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <h2 className="font-heading text-base font-bold text-foreground">{myTeam.name}</h2>
          </div>
          {myTeam.usernames.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {myTeam.usernames.map((username) => (
                <li
                  key={username}
                  className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                >
                  {username}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mb-6 overflow-hidden rounded-xl border border-[#e8e4df] bg-white">
        <div className="flex items-center gap-1.5 border-b border-[#e8e4df] bg-muted/30 px-4 py-2.5">
          {hackathon.status === 'live' && (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500 animate-pulse" />
          )}
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            {hackathon.status === 'live' ? 'Live Leaderboard' : 'Leaderboard'}
          </p>
        </div>
        <div className="p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="h-auto px-0 py-1">Rank</TableHead>
                <TableHead className="h-auto px-0 py-1">Team</TableHead>
                <TableHead className="h-auto px-0 py-1 text-right">Points</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leaderboard?.map((entry) => (
                <TableRow
                  key={entry.team_id}
                  className={
                    entry.team_id === myTeam?.team_id
                      ? 'border-l-2 border-l-blue-500 bg-blue-50 font-semibold'
                      : undefined
                  }
                >
                  <TableCell className="px-0 py-1">#{entry.rank}</TableCell>
                  <TableCell className="px-0 py-1">
                    {entry.team_name}
                    {entry.team_id === myTeam?.team_id ? ' (you)' : ''}
                  </TableCell>
                  <TableCell className="px-0 py-1 text-right">{entry.score}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!leaderboard?.length && (
            <p className="py-4 text-center text-sm text-muted-foreground">No scores yet.</p>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-[#e8e4df] bg-white p-5">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Components</p>
        {!components?.length ? (
          <p className="text-sm text-muted-foreground">No components yet.</p>
        ) : (
          <ul className="text-sm">
            {components.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 border-t border-[#f2f0ed] py-3 first:border-t-0">
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                    {c.type === 'problem' ? (
                      <Code2 className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <FolderGit2 className="h-4 w-4 text-muted-foreground" />
                    )}
                  </span>
                  <span className="min-w-0">
                    {c.type === 'problem' && c.problem_slug ? (
                      <Link
                        to="/problems/$slug"
                        params={{ slug: c.problem_slug }}
                        search={{ hackathonComponentId: c.id }}
                        className="block truncate font-semibold text-primary hover:underline"
                      >
                        {c.problem_title ?? 'Untitled'}
                      </Link>
                    ) : (
                      <span className="block truncate font-semibold text-foreground">
                        {c.problem_title ?? c.project_title ?? 'Untitled'}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">{c.max_points} pts</span>
                  </span>
                </span>
                {c.type === 'project' && (
                  <span className="flex shrink-0 flex-col items-end gap-0.5">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={hackathon.status !== 'live'}
                      onClick={() => setSubmitComponentId(c.id)}
                    >
                      Submit
                    </Button>
                    {hackathon.status !== 'live' && (
                      <span className="text-[11px] text-muted-foreground">
                        {hackathon.status === 'completed' ? 'Submissions closed' : 'Opens when live'}
                      </span>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {submitComponentId && (
        <ProjectSubmissionDialog
          batchId={batchId}
          hackathonId={hackathonId}
          componentId={submitComponentId}
          open
          onOpenChange={(next) => {
            if (!next) setSubmitComponentId(null);
          }}
          hasTeam={!!myTeam}
        />
      )}
    </div>
  );
}
